// src/components/ResultDisplay.js

import React from 'react';
import { 
    DocumentTextIcon, 
    ArrowTopRightOnSquareIcon, 
    DocumentDuplicateIcon,
    LinkIcon,
    CodeBracketIcon
} from '@heroicons/react/24/solid';

const ResultDisplay = ({ response, responseType, creditsUsed }) => {
    if (!response) return null;
    
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // Could add a toast notification here
    };
    
    const renderTextResponse = (text) => (
        <div className="p-3">
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center">
                    <DocumentTextIcon className="h-4 w-4 text-blue-500 mr-1" />
                    <span className="text-sm font-semibold">Response</span>
                </div>
                <button 
                    onClick={() => copyToClipboard(text)}
                    className="text-gray-500 hover:text-gray-700"
                    title="Copy to clipboard"
                >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                </button>
            </div>
            <div className="max-h-60 overflow-y-auto text-sm whitespace-pre-wrap">
                {text}
            </div>
        </div>
    );
    
    const renderUrlResponse = (url) => (
        <div className="p-3">
            <div className="text-center">
                <p className="mb-2 text-sm">Processing completed successfully!</p>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded text-sm"
                >
                    View Result
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                </a>
            </div>
        </div>
    );
    
    const renderUrlArrayResponse = (urls) => (
        <div className="p-3">
            <div className="mb-2 flex items-center">
                <LinkIcon className="h-4 w-4 text-blue-500 mr-1" />
                <span className="text-sm font-semibold">Generated Files</span>
            </div>
            <div className="max-h-60 overflow-y-auto">
                {urls.map((url, index) => (
                    <div key={index} className="mb-2">
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-blue-500 hover:text-blue-700 text-sm"
                        >
                            File {index + 1}
                            <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
    
    const renderJsonResponse = (json) => {
        let formattedJson;
        try {
            if (typeof json === 'string') {
                formattedJson = json;
            } else {
                formattedJson = JSON.stringify(json, null, 2);
            }
        } catch (error) {
            formattedJson = "Error formatting JSON";
        }
        
        return (
            <div className="p-3">
                <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                        <CodeBracketIcon className="h-4 w-4 text-blue-500 mr-1" />
                        <span className="text-sm font-semibold">JSON Response</span>
                    </div>
                    <button 
                        onClick={() => copyToClipboard(formattedJson)}
                        className="text-gray-500 hover:text-gray-700"
                        title="Copy to clipboard"
                    >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                </div>
                <pre className="max-h-60 overflow-y-auto text-xs bg-gray-50 p-2 rounded-md">
                    {formattedJson}
                </pre>
            </div>
        );
    };
    
    const renderCompanyJsonResponse = (results) => {
        return (
            <div className="p-3">
                <div className="mb-2 flex items-center">
                    <CodeBracketIcon className="h-4 w-4 text-blue-500 mr-1" />
                    <span className="text-sm font-semibold">Company Results</span>
                </div>
                <div className="max-h-60 overflow-y-auto">
                    {Array.isArray(results) ? (
                        results.map((company, index) => (
                            <div key={index} className="mb-3 p-2 border border-gray-200 rounded-md">
                                <h3 className="font-medium text-sm">{company.name}</h3>
                                {company.website && (
                                    <a 
                                        href={company.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-500 hover:underline"
                                    >
                                        {company.domain}
                                    </a>
                                )}
                                <div className="mt-1 text-xs text-gray-600">
                                    <p>{company.industry} • Founded {company.founded}</p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-600">No results found.</p>
                    )}
                </div>
            </div>
        );
    };
    
    let content;
    
    switch (responseType) {
        case 'text':
        case 'claude-text':
            content = renderTextResponse(response);
            break;
        
        case 'url':
            content = renderUrlResponse(response);
            break;
        
        case 'url-array':
            content = renderUrlArrayResponse(response);
            break;
        
        case 'json':
            content = renderJsonResponse(response);
            break;
        
        case 'company-json':
            content = renderCompanyJsonResponse(response);
            break;
            
        default:
            // Handle fallbacks for backward compatibility
            if (typeof response === 'string') {
                if (response.startsWith('http')) {
                    content = renderUrlResponse(response);
                } else {
                    content = renderTextResponse(response);
                }
            } else if (Array.isArray(response)) {
                content = renderUrlArrayResponse(response);
            } else {
                content = renderJsonResponse(response);
            }
    }
    
    return (
        <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
            {content}
            
            {creditsUsed > 0 && (
                <div className="bg-gray-50 p-2 text-xs text-gray-600 border-t border-gray-200">
                    {creditsUsed} credits used for this operation. 
                    <a 
                        href="https://apilabz.com/profile" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline ml-1"
                    >
                        Get more credits
                    </a>
                </div>
            )}
        </div>
    );
};

export default ResultDisplay;